/*
 * castle-rat-cursor.js — the rat cursor, as a pointer-following element.
 *
 * The rat (128×71) is far over the size browsers reliably render through
 * `cursor: url()` — Chrome swaps any >32px cursor image to the default arrow
 * whenever its bitmap would extend past the window edge, which turned the
 * footer and right edge into dead zones. So instead: hide the native cursor
 * (html.cr-rat-js in castle-rat.css) and move a fixed-position element with
 * the pointer. No size cap, no edge suppression.
 *
 * Over links/buttons the rat "scurries": three frames (base / tilted up /
 * tilted down, rotated around the front feet so the tail swishes) swapped via
 * the .cr-frame-* classes. Skipped for prefers-reduced-motion; the follower
 * itself still runs (it IS the cursor — it has to move).
 *
 * Without JS, none of this initializes and the universal `cursor: url()`
 * fallback in castle-rat.css applies. Bails on touch-only devices and in the
 * theme editor. The native cursor reappears over cross-origin iframes
 * (YouTube embeds) — we hide the follower there to avoid a frozen ghost rat.
 */
(function () {
  if (!window.matchMedia('(pointer: fine)').matches) return;
  if (window.Shopify && window.Shopify.designMode) return;

  var HOT_X = 2; // nose tip, matches the url() hotspot in castle-rat.css
  var HOT_Y = 7;
  var FRAME_MS = 130;
  var INTERACTIVE =
    'a[href], button, [role="button"], summary, input[type="submit"], input[type="button"], label[for]';
  var FRAME_CLASSES = ['', 'cr-frame-up', 'cr-frame-down'];
  // Rock cycle: up, base, down, base…
  var SEQUENCE = [1, 0, 2, 0];

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Pre-decode the tilt frames (the base frame loads with the element) so the
  // first hover doesn't flash an empty follower.
  var data = (document.currentScript && document.currentScript.dataset) || {};
  [data.up, data.down].forEach(function (url) {
    if (!url) return;
    var img = new Image();
    img.src = url;
  });

  var rat = document.createElement('div');
  rat.className = 'cr-rat-cursor cr-hidden';
  rat.setAttribute('aria-hidden', 'true');

  function init() {
    document.body.appendChild(rat);
    document.documentElement.classList.add('cr-rat-js');
  }
  if (document.body) init();
  else document.addEventListener('DOMContentLoaded', init);

  // --- follow the pointer -------------------------------------------------
  // Transform is set directly in the handler — browsers already coalesce
  // pointermove to the frame rate, and gating on requestAnimationFrame
  // permanently froze the rat when a frame never fired (occluded window,
  // background tab): the pending-rAF latch never cleared, so every later
  // move was skipped.
  document.addEventListener(
    'pointermove',
    function (event) {
      rat.classList.remove('cr-hidden');
      rat.style.transform =
        'translate3d(' +
        (event.clientX - HOT_X) +
        'px,' +
        (event.clientY - HOT_Y) +
        'px,0)';
    },
    { passive: true }
  );

  // Hide when the pointer leaves the page or the window loses focus, and
  // over iframes (their documents own the cursor; the follower would freeze).
  document.addEventListener('pointerout', function (event) {
    if (!event.relatedTarget) rat.classList.add('cr-hidden');
  });
  window.addEventListener('blur', function () {
    rat.classList.add('cr-hidden');
  });
  document.addEventListener('pointerover', function (event) {
    if (event.target.tagName === 'IFRAME') rat.classList.add('cr-hidden');
  });

  // Native HTML5 drags break the illusion: the OS draws its own hand/drag
  // cursor (CSS can't override it) and pointermove stops, freezing the rat —
  // most visible when click-drag-panning the zoomed product image. Images and
  // links are draggable by default, so suppress their drags; if a drag starts
  // anyway (e.g. a text selection), hide the rat until it ends.
  document.addEventListener('dragstart', function (event) {
    var el = event.target;
    if (el.closest && el.closest('img, a')) {
      event.preventDefault();
      return;
    }
    rat.classList.add('cr-hidden');
  });
  document.addEventListener('dragend', function () {
    rat.classList.remove('cr-hidden');
  });

  // --- scurry over interactive elements ------------------------------------
  if (reduceMotion) return;

  var timer = null;

  function setFrame(i) {
    rat.classList.remove('cr-frame-up', 'cr-frame-down');
    var cls = FRAME_CLASSES[i];
    if (cls) rat.classList.add(cls);
  }

  function stop() {
    if (timer) clearInterval(timer);
    timer = null;
    setFrame(0);
  }

  function start() {
    if (timer) return;
    var step = 0;
    setFrame(SEQUENCE[step]);
    timer = setInterval(function () {
      step = (step + 1) % SEQUENCE.length;
      setFrame(SEQUENCE[step]);
    }, FRAME_MS);
  }

  document.addEventListener('pointerover', function (event) {
    if (event.target.closest(INTERACTIVE)) start();
    else stop();
  });
})();
